import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { MdArrowBack, MdCheckCircle, MdCalendarToday, MdAccessTime, MdPerson } from 'react-icons/md';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DOWS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function genDates(n = 8) {
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() + i); return d;
  });
}

function genSlots(s = '08:00', e = '18:00', step = 10) {
  const slots = [];
  const [sh, sm] = s.split(':').map(Number);
  const [eh, em] = e.split(':').map(Number);
  let m = sh * 60 + sm;
  const end = eh * 60 + em;
  while (m < end) {
    slots.push(`${String(Math.floor(m/60)).padStart(2,'0')}:${String(m%60).padStart(2,'0')}`);
    m += step;
  }
  return slots;
}

function isPast(date, slot) {
  const now = new Date();
  const todayStart = new Date(); todayStart.setHours(0,0,0,0);
  const d = new Date(date); d.setHours(0,0,0,0);
  if (d.getTime() > todayStart.getTime()) return false;
  const [h, min] = slot.split(':').map(Number);
  return h < now.getHours() || (h === now.getHours() && min <= now.getMinutes());
}

function isOpDay(date, opDays) {
  return (opDays || []).includes(DOWS[date.getDay()]);
}

function fmtDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function fmtSlot(key) {
  const [hh, mm] = key.split(':').map(Number);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return key;
  const ampm = hh < 12 ? 'AM' : 'PM';
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
}

// Token number display
function TokenBadge({ token }) {
  if (!token) return null;
  return (
    <div style={{
      background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
      borderRadius: 16, padding: '20px 24px', textAlign: 'center', marginBottom: 20,
    }}>
      <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.6)', fontWeight: 700, marginBottom: 4 }}>YOUR TOKEN NUMBER</div>
      <div style={{ fontSize: 56, fontWeight: 900, color: '#0a0f1e', lineHeight: 1 }}>#{token}</div>
      <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.6)', marginTop: 4 }}>Show this at the reception</div>
    </div>
  );
}

export default function OpBookingPage() {
  const { hospitalId } = useParams();
  const { state }      = useLocation();
  const navigate       = useNavigate();
  const { user, showToast } = useAuth();

  const hospital   = state?.hospital;
  const patDetails = state?.patientDetails; // passed from HospitalDetailPage modal

  const opStart = hospital?.opStart || '08:00';
  const opEnd   = hospital?.opEnd   || '18:00';
  const opDays  = hospital?.opDays  || ['Mon','Tue','Wed','Thu','Fri','Sat'];
  const opWeeklySlots = hospital?.opWeeklySlots || {};

  const dates = genDates(8);
  // Only show slots the hospital explicitly saved — never auto-generate from opStart/opEnd.
  const slotsForDate = (d) => {
    const dow = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
    const wk  = opWeeklySlots[dow];
    return Array.isArray(wk) && wk.length > 0 ? wk.slice().sort() : [];
  };
  const hasPublishedSlots = Object.values(opWeeklySlots).some(
    arr => Array.isArray(arr) && arr.length > 0
  );

  const [selDate,  setSelDate]  = useState(dates[0]);
  const [selSlot,  setSelSlot]  = useState('');
  const [problem,  setProblem]  = useState('');
  const [booking,  setBooking]  = useState(false);
  const [result,   setResult]   = useState(null); // booking result

  // Patient details from modal (pre-filled)
  const [patName,   setPatName]   = useState(patDetails?.name   || user?.name || '');
  const [patAge,    setPatAge]    = useState(patDetails?.age     ? String(patDetails.age) : '');
  const [patGender, setPatGender] = useState(patDetails?.gender  || 'Male');
  const [patAddr,   setPatAddr]   = useState(patDetails?.address || '');

  // ── Booking-summary modal + promo code ─────────────────────────────
  const [showSummary, setShowSummary] = useState(false);
  const [showServiceBreakdown, setShowServiceBreakdown] = useState(false);
  const [promoInput,    setPromoInput]    = useState('');
  const [promoApplying, setPromoApplying] = useState(false);
  const [appliedPromo,  setAppliedPromo]  = useState(null); // { code, discount, message }
  const [promoError,    setPromoError]    = useState('');

  useEffect(() => { if (!user) navigate('/login'); }, [user, navigate]);
  if (!user) return null;

  const ds       = fmtDate(selDate);
  const isToday  = isOpDay(selDate, opDays);
  const slots    = isToday ? slotsForDate(selDate).filter(s => !isPast(selDate, s)) : [];

  // ── Helper: actually create the booking (called AFTER payment) ───────
  const createBooking = async (paymentRefs = {}) => {
    const phone = (user.phone || '').replace(/\D/g, '');
    const r = await api.bookWalkin({
      patientId:      user.patientId || phone,
      patientName:    patName.trim(),
      patientAge:     Number(patAge),
      patientGender:  patGender,
      patientAddress: patAddr,
      phone:          user.phone || '',
      hospitalId,
      hospitalName:   hospital?.name || '',
      date:           ds,
      slot:           selSlot,
      problem:        problem.trim(),
      bookingType:    'walkin',
      consultationFee: Number(hospital?.opFee ?? hospital?.consultationFee ?? 0),
      paymentStatus:  Object.keys(paymentRefs).length ? 'paid' : 'free',
      ...paymentRefs,
    });
    setBooking(false);
    if (r.error) {
      showToast('Booking failed: ' + r.error, 'error');
      return;
    }
    setResult(r.data);
  };

  // ── Fee math (matches Flutter app) ─────────────────────────────────
  const consultFee  = Number(hospital?.opFee ?? hospital?.consultationFee ?? 0);
  const platformFee = Math.round(consultFee * 0.10 * 100) / 100;
  const gst         = Math.round(platformFee * 0.18 * 100) / 100;
  const serviceTotal= Math.round((platformFee + gst) * 100) / 100;
  const promoDisc   = Number(appliedPromo?.discount || 0);
  const totalPayable= Math.max(0, Math.round((consultFee + serviceTotal - promoDisc) * 100) / 100);

  // ── Promo apply ────────────────────────────────────────────────────
  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) { setPromoError('Enter a promo code'); return; }
    setPromoApplying(true); setPromoError('');
    const r = await api.validatePromo({
      code,
      amount: consultFee,
      hospitalId,
      patientId: user.patientId || (user.phone || '').replace(/\D/g, ''),
    });
    setPromoApplying(false);
    if (r.error || !r.data?.valid) {
      setPromoError(r.error || r.data?.message || 'Invalid promo code');
      setAppliedPromo(null);
      return;
    }
    setAppliedPromo({
      code,
      discount: Number(r.data.discount || 0),
      message:  r.data.message || `Promo applied: ₹${r.data.discount} off`,
    });
    showToast(`Promo "${code}" applied!`, 'success');
  };

  const removePromo = () => { setAppliedPromo(null); setPromoInput(''); setPromoError(''); };

  // ── Step 1: validate form, open summary modal ──────────────────────
  const openSummary = () => {
    if (!selSlot)        { showToast('Please select a time slot', 'error'); return; }
    if (!patName.trim()) { showToast('Patient name is required',  'error'); return; }
    if (!patAge)         { showToast('Patient age is required',    'error'); return; }

    // Free consultation → skip summary, book directly
    if (consultFee <= 0) {
      setBooking(true);
      createBooking({});
      return;
    }
    setShowSummary(true);
  };

  // ── Step 2: open Razorpay with TOTAL amount, book on success ───────
  const payAndBook = async () => {
    setBooking(true);
    const fee = totalPayable;
    if (fee <= 0) {
      setShowSummary(false);
      await createBooking({ appliedCouponId: appliedPromo?.code || '' });
      return;
    }

    // Create Razorpay order
    const phoneDigits = (user.phone || '').replace(/\D/g, '');
    const orderRes = await api.createRazorpayOrder({
      amount:        fee,
      currency:      'INR',
      hospitalId,
      customerId:    user.patientId || phoneDigits || 'guest',
      customerName:  patName.trim() || user.name || 'HCEN Patient',
      customerPhone: user.phone || phoneDigits || '',
      customerEmail: user.email || '',
      purpose:       'OP_CONSULTATION',
      notes: {
        hospitalName: hospital?.name || '',
        date: ds,
        slot: selSlot,
        patientName: patName.trim(),
      },
    });
    const orderId = orderRes.data?.order_id || orderRes.data?.orderId;
    if (orderRes.error || !orderId) {
      setBooking(false);
      showToast('Payment init failed: ' + (orderRes.error || 'no order'), 'error');
      return;
    }

    // Step 2: open Razorpay checkout
    if (!window.Razorpay) {
      setBooking(false);
      showToast('Payment SDK not loaded. Please refresh the page.', 'error');
      return;
    }

    const rzp = new window.Razorpay({
      key:      orderRes.data.key_id || orderRes.data.keyId || process.env.REACT_APP_RAZORPAY_KEY || '',
      order_id: orderId,
      amount:   Math.round(fee * 100),
      currency: 'INR',
      name:     hospital?.name || 'HCEN Hospital',
      description: 'OP Consultation Fee',
      prefill: {
        name:    patName.trim(),
        contact: user.phone || '',
        email:   user.email || '',
      },
      theme: { color: '#00b894' },
      handler: async (response) => {
        // Step 3: verify on server
        const v = await api.verifyRazorpayPayment({
          razorpay_order_id:   response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature:  response.razorpay_signature,
        });
        if (v.error) {
          setBooking(false);
          showToast('Payment verification failed: ' + v.error, 'error');
          return;
        }
        // Step 4: create the booking
        setShowSummary(false);
        showToast('Payment successful! Confirming booking…', 'success');
        await createBooking({
          razorpayOrderId:   response.razorpay_order_id,
          razorpayPaymentId: response.razorpay_payment_id,
          amountPaid:        fee,
          platformFee, gst, serviceCharges: serviceTotal,
          appliedCouponId:   appliedPromo?.code || '',
          discountAmount:    promoDisc,
        });
      },
      modal: {
        ondismiss: () => {
          setBooking(false);
          showToast('Payment cancelled. Slot not booked.', 'info');
        },
      },
    });
    rzp.on('payment.failed', (resp) => {
      setBooking(false);
      showToast('Payment failed: ' + (resp.error?.description || 'unknown'), 'error');
    });
    rzp.open();
  };

  // ── Success Screen ──────────────────────────────────────────────────
  if (result) return (
    <div className="page-sm fade-in" style={{ paddingTop: 40 }}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <MdCheckCircle style={{ fontSize: 72, color: 'var(--success)', marginBottom: 12 }} />
        <div style={{ fontSize: 24, fontWeight: 900, marginBottom: 4 }}>Booking Confirmed!</div>
        <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>
          {hospital?.name || result.hospitalName || 'Hospital'}
        </div>
        {result.otp && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            Show the OTP below at the hospital reception
          </div>
        )}
      </div>

      {/* OTP Card */}
      {result.otp && (
        <div onClick={() => { navigator.clipboard?.writeText(result.otp); }}
          style={{ background: 'linear-gradient(135deg, #1565C0, #0D47A1)', borderRadius: 16, padding: '20px 24px', marginBottom: 16, textAlign: 'center', cursor: 'pointer', boxShadow: '0 8px 24px rgba(21,101,192,0.4)' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', letterSpacing: 1, marginBottom: 8, fontWeight: 600 }}>YOUR HOSPITAL OTP</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
            {result.otp.split('').map((d, i) => (
              <div key={i} style={{ width: 48, height: 54, background: 'rgba(255,255,255,0.15)', borderRadius: 10, border: '1.5px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 26, fontWeight: 900, color: '#fff' }}>{d}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 10 }}>Tap to copy</div>
        </div>
      )}

      <TokenBadge token={result.tokenNumber || result.token} />

      {/* Booking ID */}
      <div className="glass" style={{ padding: '14px 18px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, letterSpacing: 1 }}>BOOKING ID</div>
        <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--red)', letterSpacing: 1, fontFamily: 'monospace' }}>{result.bookingId || result.bookingIds || '—'}</div>
      </div>

      <div className="glass glass-p" style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 12 }}>APPOINTMENT DETAILS</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { icon: '\uD83D\uDC64', label: 'Patient', value: patName },
            { icon: '\uD83C\uDFE5', label: 'Hospital', value: hospital?.name || result.hospitalName || 'Hospital' },
            { icon: '\uD83D\uDCC5', label: 'Date', value: ds },
            { icon: '\u23F0', label: 'Slot', value: selSlot },
          ].map(row => (
            <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 16 }}>{row.icon}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 13, minWidth: 60 }}>{row.label}</span>
              <span style={{ fontWeight: 700, fontSize: 13, flex: 1 }}>{row.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Payment receipt */}
      {Number(hospital?.opFee ?? hospital?.consultationFee ?? 0) > 0 && (
        <div className="glass glass-p" style={{ marginBottom: 16, background: 'rgba(0,212,170,0.06)', border: '1px solid rgba(0,212,170,0.25)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 1 }}>PAYMENT</div>
              <div style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 700, marginTop: 2 }}>✅ Paid via Razorpay</div>
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--primary)' }}>₹{Number(hospital.opFee ?? hospital.consultationFee).toFixed(0)}</div>
          </div>
        </div>
      )}
      <button className="btn btn-primary btn-full" style={{ marginBottom: 10 }}
        onClick={() => navigate('/bookings')}>View My Bookings</button>
      <button className="btn btn-secondary btn-full" onClick={() => navigate('/')}>Go Home</button>
    </div>
  );

  // ── Booking Form ────────────────────────────────────────────────────
  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{
        padding: '14px 20px', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 12,
        background: 'rgba(255,255,255,0.02)',
        position: 'sticky', top: 0, zIndex: 10, backdropFilter: 'blur(12px)',
      }}>
        <button onClick={() => navigate(-1)} className="btn btn-ghost btn-sm" style={{ padding: 8 }}>
          <MdArrowBack size={20} />
        </button>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Walk-in OP</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {hospital?.name || 'Hospital'}
          </div>
        </div>
      </div>

      <div className="page-sm">

        {/* Patient Details (pre-filled from modal) */}
        <div className="glass glass-p" style={{ marginBottom: 24 }}>
          <div style={{ fontWeight: 700, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <MdPerson style={{ color: 'var(--primary)' }} size={18} /> Patient Details
          </div>

          <div className="input-group">
            <label className="input-label">Patient Name *</label>
            <input className="input" placeholder="Full name"
              value={patName} onChange={e => setPatName(e.target.value)} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="input-group">
              <label className="input-label">Age *</label>
              <input className="input" type="number" placeholder="e.g. 28" min="1" max="130"
                value={patAge} onChange={e => setPatAge(e.target.value)} />
            </div>
            <div className="input-group">
              <label className="input-label">Gender</label>
              <select className="input" value={patGender} onChange={e => setPatGender(e.target.value)}>
                <option>Male</option>
                <option>Female</option>
                <option>Other</option>
              </select>
            </div>
          </div>

          <div className="input-group" style={{ marginBottom: 0 }}>
            <label className="input-label">Address (optional)</label>
            <input className="input" placeholder="Street, City"
              value={patAddr} onChange={e => setPatAddr(e.target.value)} />
          </div>
        </div>

        {/* Date Selection */}
        <div style={{ fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          <MdCalendarToday style={{ color: 'var(--primary)' }} /> Select Date
        </div>
        <div className="date-strip" style={{ marginBottom: 24 }}>
          {dates.map((d, i) => {
            const op = isOpDay(d, opDays);
            const active = d.toDateString() === selDate.toDateString();
            return (
              <div key={i} className={`date-chip${active ? ' active' : ''}`}
                onClick={() => { setSelDate(d); setSelSlot(''); }}
                style={{ opacity: op ? 1 : 0.4 }}>
                <div className="dow">{DOWS[d.getDay()]}</div>
                <div className="dom">{d.getDate()}</div>
                <div className="mon">{MONTHS[d.getMonth()]}</div>
                {!op && <div style={{ fontSize: 8, color: 'var(--danger)', fontWeight: 700 }}>No OP</div>}
              </div>
            );
          })}
        </div>

        {/* Slot Selection */}
        <div style={{ fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          <MdAccessTime style={{ color: 'var(--primary)' }} /> Available Slots
        </div>
        {!isToday ? (
          <div className="glass glass-p" style={{ textAlign: 'center', marginBottom: 20, color: 'var(--text-muted)' }}>
            🌙 No OP on {DOWS[selDate.getDay()]}s — OP days: {opDays.join(', ')}
          </div>
        ) : slots.length === 0 ? (
          <div className="glass glass-p" style={{ textAlign: 'center', marginBottom: 20, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            {!hasPublishedSlots
              ? '🏥 Walk-in OP is not open yet — the hospital has not saved weekly OP slots.'
              : slotsForDate(selDate).length === 0
                ? 'No walk-in OP slots on this day. Try another date.'
                : "All slots for today have passed. Select tomorrow or a future date."}
          </div>
        ) : (
          <div className="slot-grid" style={{ marginBottom: 24 }}>
            {slots.map(s => (
              <button key={s} className={`slot-btn${selSlot === s ? ' selected' : ''}`}
                onClick={() => setSelSlot(s === selSlot ? '' : s)}
                title={s}>
                {fmtSlot(s)}
              </button>
            ))}
          </div>
        )}

        {/* Problem description */}
        <div className="input-group">
          <label className="input-label">Describe Your Problem (optional)</label>
          <textarea className="input" rows={3}
            placeholder="e.g. Fever, chest pain, follow-up…"
            value={problem} onChange={e => setProblem(e.target.value)}
            style={{ resize: 'vertical' }} />
        </div>

        {/* Summary */}
        {selSlot && (
          <div className="glass glass-p" style={{ marginBottom: 16, background: 'rgba(0,212,170,0.05)', border: '1px solid rgba(0,212,170,0.2)' }}>
            <div style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 700, marginBottom: 8 }}>Booking Summary</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.9 }}>
              <div>🏥 {hospital?.name}</div>
              <div>📅 {ds} · ⏰ {selSlot}</div>
              <div>👤 {patName || 'Patient'} · 🎂 {patAge}yr · {patGender}</div>
            </div>
          </div>
        )}

        <button className="btn btn-primary btn-full btn-lg"
          onClick={openSummary} disabled={booking || !selSlot}>
          {booking ? '⏳ Processing…' : !selSlot ? 'Select a slot to continue'
            : consultFee > 0 ? `Continue — ${selSlot}`
            : `✅ Confirm Walk-in — ${selSlot}`}
        </button>
        {consultFee > 0 && (
          <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
            🔒 Secured by Razorpay · Slot is booked only after payment
          </div>
        )}
      </div>

      {/* ── Booking Summary Modal (matches Flutter app) ───────────────── */}
      {showSummary && (
        <div onClick={() => !booking && setShowSummary(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            padding: 0,
          }}>
          <div onClick={e => e.stopPropagation()} className="fade-in"
            style={{
              background: 'var(--bg)', width: '100%', maxWidth: 520,
              borderTopLeftRadius: 24, borderTopRightRadius: 24,
              maxHeight: '92vh', overflowY: 'auto',
              boxShadow: '0 -16px 48px rgba(0,0,0,0.4)',
            }}>
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 2 }}>
              <button onClick={() => !booking && setShowSummary(false)} className="btn btn-ghost btn-sm" style={{ padding: 6 }} disabled={booking}>
                <MdArrowBack size={20} />
              </button>
              <div style={{ flex: 1, fontWeight: 800, fontSize: 17, textAlign: 'center', marginRight: 32 }}>Booking Summary</div>
            </div>

            <div style={{ padding: 18 }}>
              {/* Appointment card */}
              <div className="glass glass-p" style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 1, marginBottom: 10 }}>APPOINTMENT DETAILS</div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--primary-glow)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🏥</div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 16 }}>{hospital?.name || 'Hospital'}</div>
                    <div style={{ fontSize: 12, color: 'var(--secondary)' }}>Walk-in Consultation</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ background: 'rgba(0,123,255,0.08)', color: 'var(--blue, #1565C0)', padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700 }}>📅 {ds}</span>
                  <span style={{ background: 'rgba(0,123,255,0.08)', color: 'var(--blue, #1565C0)', padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700 }}>⏰ {selSlot}</span>
                </div>
              </div>

              {/* Patient card */}
              <div className="glass glass-p" style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 1, marginBottom: 6 }}>FOR</div>
                <div style={{ fontWeight: 800, fontSize: 16 }}>{patName}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  {user.phone || ''} {patAge && `· ${patAge}yr`} {patGender && `· ${patGender}`}
                </div>
              </div>

              {/* Charges card */}
              <div className="glass glass-p" style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 14 }}>
                  <span>Consultation Fee</span>
                  <span style={{ fontWeight: 700 }}>₹{consultFee.toFixed(2)}</span>
                </div>

                <div onClick={() => setShowServiceBreakdown(s => !s)}
                  style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 14, cursor: 'pointer', userSelect: 'none' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    Service Charges <span style={{ fontSize: 10, transition: 'transform 0.2s', transform: showServiceBreakdown ? 'rotate(180deg)' : 'none' }}>▾</span>
                  </span>
                  <span style={{ fontWeight: 700 }}>₹{serviceTotal.toFixed(2)}</span>
                </div>
                {showServiceBreakdown && (
                  <div style={{ paddingLeft: 12, paddingBottom: 8, borderLeft: '2px solid var(--border)', marginLeft: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                      <span>Platform Fee (10%)</span><span>₹{platformFee.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                      <span>GST (18% on platform fee)</span><span>₹{gst.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {/* Promo */}
                <div style={{ borderTop: '1px dashed var(--border)', paddingTop: 12, marginTop: 8 }}>
                  {!appliedPromo ? (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                        🎟️ Have a promo code?
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input className="input" value={promoInput}
                          onChange={e => { setPromoInput(e.target.value.toUpperCase()); setPromoError(''); }}
                          placeholder="Enter code"
                          style={{ flex: 1, textTransform: 'uppercase' }} />
                        <button className="btn btn-primary"
                          onClick={applyPromo}
                          disabled={promoApplying || !promoInput.trim()}
                          style={{ background: '#f59e0b', color: '#fff', minWidth: 80 }}>
                          {promoApplying ? '…' : 'Apply'}
                        </button>
                      </div>
                      {promoError && <div style={{ color: 'var(--danger, #ef4444)', fontSize: 12, marginTop: 6 }}>{promoError}</div>}
                    </>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', padding: '8px 12px', borderRadius: 10 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--success, #16a34a)' }}>✓ {appliedPromo.code}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>−₹{promoDisc.toFixed(2)} discount</div>
                      </div>
                      <button onClick={removePromo} className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}>Remove</button>
                    </div>
                  )}
                </div>

                {/* Total */}
                <div style={{ borderTop: '1px solid var(--border)', marginTop: 14, paddingTop: 12, display: 'flex', justifyContent: 'space-between', fontSize: 17, fontWeight: 900 }}>
                  <span>Total Payable</span>
                  <span>₹{totalPayable.toFixed(2)}</span>
                </div>
              </div>

              {/* Pay button */}
              <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
                🔒 Paying ₹{totalPayable.toFixed(2)} via Razorpay (Secured)
              </div>
              <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 10, padding: '8px 12px', fontSize: 12, color: 'var(--success, #16a34a)', textAlign: 'center', marginBottom: 14, fontWeight: 600 }}>
                ✓ HCEN Promise · Instant confirmation · Refund if doctor cancels
              </div>
              <button className="btn btn-primary btn-full btn-lg"
                onClick={payAndBook} disabled={booking}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                💳 {booking ? 'Processing…' : 'Proceed to Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
